import { defaultEnabledEditorState, PanelType, PopoverActiveState, PopoverType } from '@/constants/app-options'
import { SubMenuType } from '@/constants/editor'
import {
  DRAGGABLE_TYPE,
  ObjectType,
  SAVE_PROJECT_STATE,
  ToolType,
} from '@/scenes/engine/common/constants'
import { setDisableEditorState } from '@/store/slices/editor/action'
import { useAppDispatch } from '@/store/store'
import React, { createContext, useState, FC, ReactNode, useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'

type Template = any
interface IAppContext {
  isMobile: boolean | undefined
  setIsMobile: React.Dispatch<React.SetStateAction<boolean>>
  templates: Template[]
  setTemplates: (templates: Template[]) => void
  uploads: any[]
  setUploads: (templates: any[]) => void
  shapes: any[]
  setShapes: (templates: any[]) => void
  activePanel: PanelType
  setActivePanel: (option: PanelType) => void
  activeSubMenu: SubMenuType | null
  setActiveSubMenu: (option: SubMenuType) => void
  currentTemplate: any
  setCurrentTemplate: any
  hasBgImage: boolean
  setHasBgImage: (value: boolean) => void
  isLoadedJsonSuccess: boolean
  setIsLoadedJsonSuccess: (value: boolean) => void
  objDragging: {
    item: any
    type: DRAGGABLE_TYPE
    dropped?: boolean
  } | null
  setObjDragging: (value: { item: any; type: DRAGGABLE_TYPE, dropped?: boolean }) => void
  isReplacingImage: boolean
  setIsReplacingImage: (value: boolean) => void
  isOpenPixelManipulationObject: boolean
  setisOpenPixelManipulationObject: (value: boolean) => void
  popoverActive: PopoverActiveState | null
  setPopoverActive: (value: PopoverType | PopoverActiveState | null) => void
  toolType: ToolType
  setToolType: (value: ToolType) => void
  isOpenTutorial: boolean,
  setIsOpenTutorial: (value: boolean) => void,
  isOpenFontFamily: boolean, 
  setIsOpenFontFamily: (value: boolean) => void,
  saveProjectState: SAVE_PROJECT_STATE,
  setSaveProjectState: (value: SAVE_PROJECT_STATE) => void,
  openToast: string,
  setOpenToast:(value: any) => void
  resetEditorContext: () => void,
}

interface AppProviderProps {
  children: ReactNode
  onResetEditorContext?: () => void
}

export const AppContext = createContext<IAppContext>({
  isMobile: false,
  setIsMobile: () => {},
  templates: [],
  setTemplates: () => {},
  uploads: [],
  setUploads: () => {},
  shapes: [],
  setShapes: () => {},
  activePanel: PanelType.BACKGROUND,
  setActivePanel: () => {},
  activeSubMenu: null,
  setActiveSubMenu: (value: SubMenuType) => {},
  currentTemplate: {},
  setCurrentTemplate: {},
  hasBgImage: false,
  setHasBgImage: (value: boolean) => {},
  isLoadedJsonSuccess: false,
  setIsLoadedJsonSuccess: (value: boolean) => {},
  objDragging: null,
  setObjDragging: (value: { item: any; type: DRAGGABLE_TYPE }) => {},
  isReplacingImage: false,
  setIsReplacingImage: (value: boolean) => {},
  isOpenPixelManipulationObject: false,
  setisOpenPixelManipulationObject: (value: boolean) => {},
  popoverActive: null,
  setPopoverActive: (value: PopoverType | PopoverActiveState | null) => {},
  toolType: '',
  setToolType: (value: ToolType) => {},
  isOpenTutorial: false,
  setIsOpenTutorial:  (value: boolean) => {},
  saveProjectState: SAVE_PROJECT_STATE.END,
  setSaveProjectState: (value: SAVE_PROJECT_STATE) => {},
  isOpenFontFamily: false, 
  setIsOpenFontFamily: (value: boolean) => {},
  openToast: '',
  setOpenToast: (value: any) => {},
  resetEditorContext: () => {},
})

export const AppProvider: FC<AppProviderProps> = ({ children, onResetEditorContext }) => {
  const [isMobile, setIsMobile] = useState<boolean>(undefined)
  const [templates, setTemplates] = useState<Template[]>([])
  const [uploads, setUploads] = useState<any[]>([])
  const [shapes, setShapes] = useState<Template[]>([])
  const [activePanel, setActivePanel] = useState<PanelType>(PanelType.BACKGROUND)
  const [activeSubMenu, setActiveSubMenu] = useState<SubMenuType | null>(null)
  const [currentTemplate, setCurrentTemplate] = useState(null)
  const [hasBgImage, setHasBgImage] = useState(false)
  const [isLoadedJsonSuccess, setIsLoadedJsonSuccess] = useState(true)
  const [objDragging, setObjDragging] = useState<{
    item: any
    type: DRAGGABLE_TYPE
  }>({
    item: null,
    type: ObjectType.BAZAART_IMAGE,
  })
  const [isReplacingImage, setIsReplacingImage] = useState(false)
  const [isOpenPixelManipulationObject, setisOpenPixelManipulationObject] = useState(false)
  const [popoverActive, _setPopoverActive] = useState<PopoverActiveState | null>(null)
  const setPopoverActive = useCallback((value: PopoverType | PopoverActiveState | null) => {
    if (value === null) {
      _setPopoverActive(null)
    } else if (typeof value === 'string') {
      _setPopoverActive({ type: value })
    } else {
      _setPopoverActive(value)
    }
  }, [])
  const [toolType, setToolType] = useState<ToolType>('')
  const [isOpenTutorial, setIsOpenTutorial] = useState(false)
  const [saveProjectState, setSaveProjectState] = useState(SAVE_PROJECT_STATE.INIT)
  const [isOpenFontFamily, setIsOpenFontFamily] = useState(false)
  const [openToast, setOpenToast] = useState('')
  const resetEditorContext = useCallback(() => {
    onResetEditorContext?.()
  }, [onResetEditorContext])

  const context = useMemo(() => ({
    isMobile,
    setIsMobile,
    templates,
    setTemplates,
    activePanel,
    setActivePanel,
    shapes,
    setShapes,
    activeSubMenu,
    setActiveSubMenu,
    uploads,
    setUploads,
    currentTemplate,
    setCurrentTemplate,
    hasBgImage,
    setHasBgImage,
    isLoadedJsonSuccess,
    setIsLoadedJsonSuccess,
    objDragging,
    setObjDragging,
    isReplacingImage,
    setIsReplacingImage,
    isOpenPixelManipulationObject,
    setisOpenPixelManipulationObject,
    popoverActive,
    setPopoverActive,
    toolType,
    setToolType,
    isOpenTutorial,
    setIsOpenTutorial,
    saveProjectState,
    setSaveProjectState,
    isOpenFontFamily,
    setIsOpenFontFamily,
    openToast,
    setOpenToast,
    resetEditorContext,
  }), [
    isMobile, templates, activePanel, shapes, activeSubMenu, uploads,
    currentTemplate, hasBgImage, isLoadedJsonSuccess, objDragging,
    isReplacingImage, isOpenPixelManipulationObject, popoverActive,
    toolType, isOpenTutorial,
    saveProjectState, isOpenFontFamily, openToast, resetEditorContext,
  ])
  return <AppContext.Provider value={context}>{children}</AppContext.Provider>
}
